import { useEffect, useState } from "react";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Home,
  LayoutDashboard,
  CalendarClock,
  History,
  Star,
  Briefcase,
  UserCircle,
  Bell,
  Shield,
  LogOut,
  Link as LinkIcon,
  LifeBuoy,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

type NavItem = {
  label: string;
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  keywords?: string;
};

const NAV: NavItem[] = [
  { label: "Home", to: "/", icon: Home, keywords: "lobby landing" },
  { label: "Dashboard", to: "/dashboard", icon: LayoutDashboard },
  { label: "Shifts", to: "/bartender", icon: CalendarClock, keywords: "feed bookings work" },
  { label: "History", to: "/history", icon: History, keywords: "past payouts earnings" },
  { label: "Favorites", to: "/favorites", icon: Star, keywords: "saved bartenders" },
  { label: "Portfolio", to: "/portfolio", icon: Briefcase },
  { label: "Account", to: "/account", icon: UserCircle, keywords: "settings profile" },
  { label: "Notifications", to: "/notification-settings", icon: Bell },
  { label: "Admin", to: "/admin", icon: Shield, keywords: "moderation" },
];

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const routerState = useRouterState();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const inField =
        !!target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.tagName === "SELECT" ||
          target.isContentEditable);

      if ((e.key === "k" || e.key === "K") && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((o) => !o);
        return;
      }
      if (e.key === "/" && !inField) {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const run = (fn: () => void | Promise<void>) => {
    setOpen(false);
    void fn();
  };

  const go = (to: string) => run(() => navigate({ to: to as never }));

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Search the Reserve  —  type a command or page…" />
      <CommandList>
        <CommandEmpty>Nothing found.</CommandEmpty>

        <CommandGroup heading="Navigate">
          {NAV.map((item) => {
            const Icon = item.icon;
            return (
              <CommandItem
                key={item.to}
                value={`${item.label} ${item.keywords ?? ""}`}
                onSelect={() => go(item.to)}
              >
                <Icon className="mr-2 h-4 w-4 opacity-70" />
                <span>{item.label}</span>
                <span className="ml-auto text-[10px] tracking-wide-luxury text-muted-foreground">
                  {item.to}
                </span>
              </CommandItem>
            );
          })}
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Actions">
          <CommandItem
            value="copy link current page url"
            onSelect={() =>
              run(async () => {
                try {
                  await navigator.clipboard.writeText(window.location.href);
                  toast.success("Link copied to clipboard.");
                } catch {
                  toast.error("Could not copy link.");
                }
              })
            }
          >
            <LinkIcon className="mr-2 h-4 w-4 opacity-70" />
            Copy current page link
            <span className="ml-auto truncate max-w-[40%] text-[10px] text-muted-foreground">
              {routerState.location.pathname}
            </span>
          </CommandItem>

          <CommandItem
            value="support help contact"
            onSelect={() =>
              run(() => {
                window.location.href = "mailto:support@reserve-agency.co.uk";
              })
            }
          >
            <LifeBuoy className="mr-2 h-4 w-4 opacity-70" />
            Contact support
          </CommandItem>

          <CommandItem
            value="sign out logout log out"
            onSelect={() =>
              run(async () => {
                await supabase.auth.signOut();
                navigate({ to: "/auth", replace: true });
              })
            }
          >
            <LogOut className="mr-2 h-4 w-4 opacity-70" />
            Sign out
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
